/**
 * Cross-channel content calendar — every video across every channel the user
 * has access to, plotted on a month grid. Supports drag-and-drop to reschedule.
 */
import { useEffect, useState, useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowLeft, Calendar as CalIcon, Loader2 } from "lucide-react";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import "./studio.css";

const API = process.env.REACT_APP_BACKEND_URL || '';

const STAGE_COLORS = {
  idea: "#7c3aed", approved: "#a78bfa", scripting: "#60a5fa",
  script_review: "#38bdf8", recording: "#34d399", raw_uploaded: "#10b981",
  editing: "#f59e0b", edit_review: "#F59E0B", thumbnail: "#fb923c",
  thumbnail_review: "#fdba74", seo: "#f472b6", scheduled: "#c084fc",
  published: "#22c55e", archived: "#6b7280",
};

export default function StudioCalendar() {
  const [data, setData] = useState({ events: [], channels: [] });
  const [loading, setLoading] = useState(true);
  const [monthOffset, setMonthOffset] = useState(0);
  const [channelFilter, setChannelFilter] = useState("");
  const [dragOverKey, setDragOverKey] = useState(null);
  const [moving, setMoving] = useState(null);

  const { start, end, label } = useMemo(() => {
    const now = new Date();
    const d = new Date(now.getFullYear(), now.getMonth() + monthOffset, 1);
    const e = new Date(d.getFullYear(), d.getMonth() + 1, 0);
    return {
      start: d.toISOString(),
      end: new Date(e.setHours(23, 59, 59)).toISOString(),
      label: d.toLocaleString("en", { month: "long", year: "numeric" }),
    };
  }, [monthOffset]);

  const reload = useCallback(() => {
    let alive = true;
    setLoading(true);
    axios
      .get(`${API}/api/studio/calendar`, { withCredentials: true, params: { start, end } })
      .then((r) => alive && setData(r.data || { events: [], channels: [] }))
      .catch(() => alive && setData({ events: [], channels: [] }))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [start, end]);

  useEffect(() => { reload(); }, [reload]);

  const events = (data.events || []).filter((e) =>
    channelFilter ? e.channel_id === channelFilter : true,
  );

  const byDay = useMemo(() => {
    const m = {};
    for (const ev of events) {
      const key = (ev.publish_date || "").slice(0, 10);
      (m[key] = m[key] || []).push(ev);
    }
    return m;
  }, [events]);

  const firstDay = new Date(start);
  const monthLen = new Date(firstDay.getFullYear(), firstDay.getMonth() + 1, 0).getDate();
  const offset = firstDay.getDay();
  const cells = [];
  for (let i = 0; i < offset; i++) cells.push(null);
  for (let d = 1; d <= monthLen; d++) {
    const dt = new Date(firstDay.getFullYear(), firstDay.getMonth(), d);
    cells.push({ day: d, key: dt.toISOString().slice(0, 10) });
  }

  const onDrop = async (e, targetKey) => {
    e.preventDefault();
    setDragOverKey(null);
    const id = e.dataTransfer.getData("text/event-id");
    const fromKey = e.dataTransfer.getData("text/from-key");
    if (!id || fromKey === targetKey) return;
    // Patch publish_date to ISO at noon UTC on the target day
    const newDate = new Date(targetKey + "T12:00:00Z").toISOString();
    setMoving(id);
    try {
      await axios.put(
        `${API}/api/studio/content/${id}`,
        { publish_date: newDate },
        { withCredentials: true },
      );
      toast.success("Rescheduled");
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not reschedule");
    } finally {
      setMoving(null);
    }
  };

  return (
    <>
      <Header />
      <div className="studio-shell">
        <Link to="/studio" className="studio-back" data-testid="cal-back">
          <ArrowLeft size={14}/> Back to Studio
        </Link>
        <div className="studio-section-h" style={{ marginTop: 12 }}>
          <div>
            <span className="studio-eyebrow"><CalIcon size={12}/> Cross-channel calendar</span>
            <h2 style={{ margin: "6px 0 0" }}>{label}</h2>
            <small style={{ color: "var(--st-text-muted)" }} data-testid="cal-dnd-hint">
              Drag any video to a different date to reschedule.
            </small>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button className="studio-btn-ghost" onClick={() => setMonthOffset((x) => x - 1)} data-testid="cal-prev">Prev</button>
            <button className="studio-btn-ghost" onClick={() => setMonthOffset(0)} data-testid="cal-today">Today</button>
            <button className="studio-btn-ghost" onClick={() => setMonthOffset((x) => x + 1)} data-testid="cal-next">Next</button>
            <select
              value={channelFilter}
              onChange={(e) => setChannelFilter(e.target.value)}
              className="studio-stage-select"
              data-testid="cal-channel-filter"
            >
              <option value="">All channels</option>
              {(data.channels || []).map((c) => (
                <option key={c.channel_id} value={c.channel_id}>{c.title}</option>
              ))}
            </select>
          </div>
        </div>

        {loading ? (
          <div className="studio-card studio-loading">
            <Loader2 size={22} className="ac-spin" style={{ color: "var(--st-purple, #a78bfa)" }}/>
          </div>
        ) : (
          <motion.div
            className="studio-cal"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            data-testid="studio-cal-grid"
          >
            {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map((d) => (
              <div key={d} className="studio-cal-head">{d}</div>
            ))}
            {cells.map((c, i) => (
              <div
                key={i}
                className={`studio-cal-cell${c ? "" : " studio-cal-cell--empty"}${dragOverKey === c?.key ? " studio-cal-cell--drop" : ""}`}
                data-testid={c ? `cal-cell-${c.key}` : undefined}
                onDragOver={c ? (e) => { e.preventDefault(); setDragOverKey(c.key); } : undefined}
                onDragLeave={c ? () => setDragOverKey(null) : undefined}
                onDrop={c ? (e) => onDrop(e, c.key) : undefined}
              >
                {c && (
                  <>
                    <small>{c.day}</small>
                    {(byDay[c.key] || []).slice(0, 4).map((ev) => (
                      <div
                        key={ev._id}
                        className="studio-cal-event"
                        draggable
                        onDragStart={(e) => {
                          e.dataTransfer.setData("text/event-id", ev._id);
                          e.dataTransfer.setData("text/from-key", c.key);
                          e.dataTransfer.effectAllowed = "move";
                        }}
                        style={{
                          background: STAGE_COLORS[ev.stage] || "#6b7280",
                          opacity: moving === ev._id ? 0.5 : 1,
                          cursor: "grab",
                        }}
                        title={`${ev.title} · ${ev.channel_title} (drag to reschedule)`}
                        data-testid={`cal-event-${ev._id}`}
                      >
                        <Link
                          to={`/studio/channels/${ev.channel_id}#content-${ev._id}`}
                          style={{ color: "#fff", textDecoration: "none", display: "block" }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {ev.title}
                        </Link>
                      </div>
                    ))}
                    {(byDay[c.key] || []).length > 4 && (
                      <span className="studio-cal-more">+{byDay[c.key].length - 4}</span>
                    )}
                  </>
                )}
              </div>
            ))}
          </motion.div>
        )}
      </div>
      <Footer />
    </>
  );
}
